// Image hosting utility - Centralized external image management
// Reduces repository size and protects against broken external links

const IMAGE_BASE_URLS = {
  // GitHub raw URLs for our hosted images (recommended primary)
  github: 'https://raw.githubusercontent.com/richardsonstrategygroup/rsg-assets/main',
  
  // Cloudinary for optimized images (optional secondary)
  cloudinary: 'https://res.cloudinary.com/rsg-strategy/image/upload',
  
  // Fallback to Unsplash for stock photos
  unsplash: 'https://images.unsplash.com'
};

// Centralized image catalog - all external images organized by category
export const HOSTED_IMAGES = {
  // Company branding assets
  brand: {
    favicon32: `${IMAGE_BASE_URLS.github}/brand/rsg-favicon-32x32.png`,
    favicon16: `${IMAGE_BASE_URLS.github}/brand/rsg-favicon-16x16.png`,
    appleTouchIcon: `${IMAGE_BASE_URLS.github}/brand/rsg-apple-touch-icon.png`,
    logo: `${IMAGE_BASE_URLS.github}/brand/rsg-logo.svg`,
    logoWhite: `${IMAGE_BASE_URLS.github}/brand/rsg-logo-white.svg`
  },
  
  // Team photos
  team: {
    andrewRichardson: `${IMAGE_BASE_URLS.github}/team/andrew-richardson-profile.jpg`,
    andrewRichardsonLarge: `${IMAGE_BASE_URLS.github}/team/andrew-richardson-profile-lg.jpg`
  },
  
  // Article and blog images
  articles: {
    aiMeasurementFeatured: `${IMAGE_BASE_URLS.github}/articles/ai-measurement-revolution-hero.jpeg`,
    adobeAiReferralChart: `${IMAGE_BASE_URLS.github}/articles/adobe-ai-referral-chart.png`,
    ecommerceGrowthHero: `${IMAGE_BASE_URLS.github}/articles/ecommerce-growth-plateau-hero.jpg`,
    consumerBehaviorHero: `${IMAGE_BASE_URLS.github}/articles/consumer-behavior-2024-hero.jpg`,
    strategicPlanningHero: `${IMAGE_BASE_URLS.github}/articles/strategic-planning-broken-hero.jpg`,
    dataDecisionHero: `${IMAGE_BASE_URLS.github}/articles/data-decision-gap-hero.jpg`
  },
  
  // Hero images by city (dynamic loading)
  heroes: {
    default: `${IMAGE_BASE_URLS.github}/heroes/strategy-default-hero.jpeg`,
    philadelphia: `${IMAGE_BASE_URLS.github}/heroes/philadelphia-hero.jpeg`,
    newyork: `${IMAGE_BASE_URLS.github}/heroes/new-york-hero.jpeg`,
    losangeles: `${IMAGE_BASE_URLS.github}/heroes/los-angeles-hero.jpeg`,
    sanfrancisco: `${IMAGE_BASE_URLS.github}/heroes/san-francisco-hero.jpeg`,
    birmingham: `${IMAGE_BASE_URLS.github}/heroes/birmingham-hero.jpeg`,
    london: `${IMAGE_BASE_URLS.github}/heroes/london-hero.jpeg`,
    chicago: `${IMAGE_BASE_URLS.github}/heroes/chicago-hero.jpeg`
  },
  
  // Case for Change images (uploaded to root of rsg-assets)
  caseForChange: {
    analytics: `${IMAGE_BASE_URLS.github}/case-for-change-analytics.jpg`,
    dataAnalysis: `${IMAGE_BASE_URLS.github}/case-for-change-data-analysis.jpeg`,
    growth: `${IMAGE_BASE_URLS.github}/case-for-change-growth.jpeg`
  },
  
  // Social media and Open Graph images
  social: {
    ogDefault: `${IMAGE_BASE_URLS.github}/social/og-default.jpg`,
    twitterCard: `${IMAGE_BASE_URLS.github}/social/twitter-card.jpg`,
    linkedinCard: `${IMAGE_BASE_URLS.github}/social/linkedin-card.jpg`
  }
};

// Legacy external URLs that need to be migrated
export const LEGACY_EXTERNAL_IMAGES = {
  // Adobe chart - critical to archive
  adobeAiChart: 'https://business.adobe.com/blog/media_1ae3eec5ac0a3277c8318b56c4bf04f0005cbdda6.png?width=750&format=png&optimize=medium',
  
  // Andrew's profile from Google Drive - migrate immediately
  andrewProfile: 'https://drive.google.com/thumbnail?id=1hEq57r79yO5ZlEmTytvX6_YBu3kaApac&sz=w150',
  andrewProfileLarge: 'https://drive.google.com/thumbnail?id=1hEq57r79yO5ZlEmTytvX6_YBu3kaApac&sz=w1000',
  
  // Unsplash images - keep as fallback but archive important ones
  aiMeasurementUnsplash: 'https://images.unsplash.com/photo-1677442136019-21780ecad995?auto=format&fit=crop&w=800&q=80',
  defaultHeroUnsplash: 'https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80'
};

// Utility functions
export const getImageUrl = (category, imageName) => {
  return HOSTED_IMAGES[category]?.[imageName] || null;
};

export const getOptimizedUrl = (imagePath, options = {}) => {
  const { width, height, quality = 80, format = 'auto' } = options;
  
  // If using Cloudinary, add optimization parameters
  if (imagePath.includes('cloudinary.com')) {
    const params = [];
    if (width) params.push(`w_${width}`);
    if (height) params.push(`h_${height}`);
    if (quality) params.push(`q_${quality}`);
    if (format) params.push(`f_${format}`);
    
    return imagePath.replace('/upload/', `/upload/${params.join(',')}/`);
  }
  
  return imagePath;
};

// Fallback function with error handling
export const getImageWithFallback = (primaryUrl, fallbackUrl) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(primaryUrl);
    img.onerror = () => resolve(fallbackUrl);
    img.src = primaryUrl;
  });
};

// Migration helper - maps old URLs to new hosted versions
export const getMigratedImageUrl = (oldUrl) => {
  const migrationMap = {
    [LEGACY_EXTERNAL_IMAGES.andrewProfile]: HOSTED_IMAGES.team.andrewRichardson,
    [LEGACY_EXTERNAL_IMAGES.andrewProfileLarge]: HOSTED_IMAGES.team.andrewRichardsonLarge,
    [LEGACY_EXTERNAL_IMAGES.adobeAiChart]: HOSTED_IMAGES.articles.adobeAiReferralChart,
    [LEGACY_EXTERNAL_IMAGES.aiMeasurementUnsplash]: HOSTED_IMAGES.articles.aiMeasurementFeatured,
    [LEGACY_EXTERNAL_IMAGES.defaultHeroUnsplash]: HOSTED_IMAGES.heroes.default
  };
  
  return migrationMap[oldUrl] || oldUrl;
};